import React, { useState } from "react";
import {
    Box,
    Flex,
    Avatar,
    VStack,
    HStack,
    Text,
    Input,
    IconButton,
    Button,
    Icon,
    Divider,
    FormLabel,
    FormControl,
    Tooltip,
    Spinner,
} from "@chakra-ui/react";
import { FiEdit2, FiCheck, FiX, FiLock, FiUser } from "react-icons/fi";
import { MdOutlineEmail, MdOutlinePersonOutline } from "react-icons/md";
import { useUser } from "context/UserContext";
import { updateUser } from "services/UserService";
import { UserProto } from "build-ts/interface";
import ForgotPassword from "components/auth/ForgotPassword";

const UserSettingPage: React.FC = () => {
    const { username, user, setUsername, setUser, logout } = useUser();

    const currentClientObject = (): Record<string, unknown> => {
        try { return JSON.parse(user?.client_object_json || "{}"); } catch { return {}; }
    };

    // --- Username edit state ---
    const [isEditingName, setIsEditingName] = useState(false);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [editedName, setEditedName] = useState(username || "");
    const [isSavingName, setIsSavingName] = useState(false);

    // --- Anko name edit state ---
    const [isEditingAnkoName, setIsEditingAnkoName] = useState(false);
    const [editedAnkoName, setEditedAnkoName] = useState(
        () => (currentClientObject().ankoUserName as string) || ""
    );
    const [isSavingAnkoName, setIsSavingAnkoName] = useState(false);
    const [ankoName, setAnkoName] = useState(
        () => (currentClientObject().ankoUserName as string) || ""
    );

    const handleCancelEdit = () => {
        setEditedName(username || "");
        setIsEditingName(false);
    };

    const handleSaveName = async () => {
        const trimmed = editedName.trim();
        if (!trimmed || trimmed === username) {
            setIsEditingName(false);
            return;
        }
        setIsSavingName(true);
        const req = UserProto.create({
            header: { ...user?.header, name: trimmed },
            email: user?.email,
            client_object_json: user?.client_object_json,
        });
        const updated = await updateUser(req);
        setIsSavingName(false);
        if (updated) {
            setUser(updated);
            setUsername(updated.header?.name || trimmed);
        }
        setIsEditingName(false);
    };

    const handleCancelAnkoEdit = () => {
        setEditedAnkoName(ankoName);
        setIsEditingAnkoName(false);
    };

    const handleSaveAnkoName = async () => {
        const trimmed = editedAnkoName.trim();
        if (trimmed === ankoName) {
            setIsEditingAnkoName(false);
            return;
        }
        setIsSavingAnkoName(true);
        const clientObj = { ...currentClientObject(), ankoUserName: trimmed };
        const req = UserProto.create({
            header: { ...user?.header },
            email: user?.email,
            is_admin: user?.is_admin,
            client_object_json: JSON.stringify(clientObj),
        });
        const updated = await updateUser(req);
        setIsSavingAnkoName(false);
        if (updated) {
            setUser(updated);
            setAnkoName(trimmed);
            setEditedAnkoName(trimmed);
        }
        setIsEditingAnkoName(false);
    };

    return (
        <Box h="100vh" overflowY="auto" bg="gray.50">
            {/* Page Header Banner */}
            <Box
                bgGradient="linear(to-r, purple.700, purple.500)"
                h="70px"
                w="full"
                position="relative"
                borderBottom="1px solid"
                borderColor="white"
            />

            {/* Content area — overlaps the banner */}
            <Box px={{ base: 6, md: 12, lg: 16 }} pb={12} mt="-100px">

                {/* ── Profile Hero Card ── */}
                <Box bg="bg.surface" borderRadius="2xl" boxShadow="md" p={8} mb={6}>
                    <Flex align="center" gap={8} wrap="wrap">
                        {/* Avatar */}
                        <Box
                            borderRadius="full"
                            border="4px solid"
                            borderColor="bg.surface"
                            boxShadow="lg"
                            flexShrink={0}
                        >
                            <Avatar
                                size="xl"
                                name={username || "User"}
                                bg="green.700"
                                color="white"
                                fontWeight="bold"
                            />
                        </Box>
                    </Flex>
                </Box>

                {/* ── Profile Information Card ── */}
                <Box bg="white" borderRadius="2xl" boxShadow="md" p={8} mb={6}>
                    <HStack spacing={2} mb={6}>
                        <Icon as={FiUser} boxSize={5} color="purple.600" />
                        <Text fontSize="lg" fontWeight="700" color="gray.700" mb={0}>
                            Profile Information
                        </Text>
                    </HStack>
                    <Divider mb={6} />

                    <VStack spacing={6} align="stretch">
                        {/* Username */}
                        <FormControl>
                            <FormLabel
                                fontSize="xs"
                                fontWeight="700"
                                color="gray.500"
                                textTransform="uppercase"
                                letterSpacing="wider"
                                mb={2}
                            >
                                <HStack spacing={1}>
                                    <Icon as={MdOutlinePersonOutline} boxSize={4} />
                                    <Text mb={0}>Username</Text>
                                </HStack>
                            </FormLabel>

                            {isEditingName ? (
                                <HStack spacing={2}>
                                    <Input
                                        value={editedName}
                                        onChange={(e) => setEditedName(e.target.value)}
                                        autoFocus
                                        size="md"
                                        borderRadius="lg"
                                        focusBorderColor="purple.500"
                                        isDisabled={isSavingName}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") handleSaveName();
                                            if (e.key === "Escape") handleCancelEdit();
                                        }}
                                    />
                                    <Tooltip label="Save" hasArrow placement="top">
                                        <IconButton
                                            aria-label="Save username"
                                            icon={isSavingName ? <Spinner size="sm" /> : <Icon as={FiCheck} />}
                                            colorScheme="purple"
                                            size="md"
                                            borderRadius="lg"
                                            onClick={handleSaveName}
                                            isDisabled={isSavingName}
                                        />
                                    </Tooltip>
                                    <Tooltip label="Cancel" hasArrow placement="top">
                                        <IconButton
                                            aria-label="Cancel edit"
                                            icon={<Icon as={FiX} />}
                                            variant="ghost"
                                            colorScheme="gray"
                                            size="md"
                                            borderRadius="lg"
                                            onClick={handleCancelEdit}
                                            isDisabled={isSavingName}
                                        />
                                    </Tooltip>
                                </HStack>
                            ) : (
                                <HStack spacing={2}>
                                    <Flex
                                        flex={1}
                                        align="center"
                                        px={4}
                                        py={2}
                                        bg="gray.50"
                                        borderRadius="lg"
                                        border="1px solid"
                                        borderColor="gray.200"
                                        minH="42px"
                                    >
                                        <Text fontSize="md" color="gray.800" fontWeight="medium" mb={0}>
                                            {username || "—"}
                                        </Text>
                                    </Flex>
                                    <Tooltip label="Edit username" hasArrow placement="top">
                                        <IconButton
                                            aria-label="Edit username"
                                            icon={<Icon as={FiEdit2} boxSize={4} />}
                                            variant="ghost"
                                            colorScheme="purple"
                                            size="md"
                                            borderRadius="lg"
                                            onClick={() => {
                                                setEditedName(username || "");
                                                setIsEditingName(true);
                                            }}
                                        />
                                    </Tooltip>
                                </HStack>
                            )}
                        </FormControl>

                        {/* What Anko Calls You */}
                        <FormControl>
                            <FormLabel
                                fontSize="xs"
                                fontWeight="700"
                                color="gray.500"
                                textTransform="uppercase"
                                letterSpacing="wider"
                                mb={2}
                            >
                                <HStack spacing={1}>
                                    <Icon as={MdOutlinePersonOutline} boxSize={4} />
                                    <Text mb={0}>What Anko Calls You</Text>
                                </HStack>
                            </FormLabel>

                            {isEditingAnkoName ? (
                                <HStack spacing={2}>
                                    <Input
                                        value={editedAnkoName}
                                        onChange={(e) => setEditedAnkoName(e.target.value)}
                                        autoFocus
                                        size="md"
                                        borderRadius="lg"
                                        focusBorderColor="purple.500"
                                        isDisabled={isSavingAnkoName}
                                        placeholder="e.g. Alex"
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") handleSaveAnkoName();
                                            if (e.key === "Escape") handleCancelAnkoEdit();
                                        }}
                                    />
                                    <Tooltip label="Save" hasArrow placement="top">
                                        <IconButton
                                            aria-label="Save Anko name"
                                            icon={isSavingAnkoName ? <Spinner size="sm" /> : <Icon as={FiCheck} />}
                                            colorScheme="purple"
                                            size="md"
                                            borderRadius="lg"
                                            onClick={handleSaveAnkoName}
                                            isDisabled={isSavingAnkoName}
                                        />
                                    </Tooltip>
                                    <Tooltip label="Cancel" hasArrow placement="top">
                                        <IconButton
                                            aria-label="Cancel Anko name edit"
                                            icon={<Icon as={FiX} />}
                                            variant="ghost"
                                            colorScheme="gray"
                                            size="md"
                                            borderRadius="lg"
                                            onClick={handleCancelAnkoEdit}
                                            isDisabled={isSavingAnkoName}
                                        />
                                    </Tooltip>
                                </HStack>
                            ) : (
                                <HStack spacing={2}>
                                    <Flex
                                        flex={1}
                                        align="center"
                                        px={4}
                                        py={2}
                                        bg="gray.50"
                                        borderRadius="lg"
                                        border="1px solid"
                                        borderColor="gray.200"
                                        minH="42px"
                                    >
                                        <Text fontSize="md" color={ankoName ? "gray.800" : "gray.400"} fontWeight="medium" mb={0}>
                                            {ankoName || "Not set"}
                                        </Text>
                                    </Flex>
                                    <Tooltip label="Edit Anko name" hasArrow placement="top">
                                        <IconButton
                                            aria-label="Edit Anko name"
                                            icon={<Icon as={FiEdit2} boxSize={4} />}
                                            variant="ghost"
                                            colorScheme="purple"
                                            size="md"
                                            borderRadius="lg"
                                            onClick={() => {
                                                setEditedAnkoName(ankoName);
                                                setIsEditingAnkoName(true);
                                            }}
                                        />
                                    </Tooltip>
                                </HStack>
                            )}
                        </FormControl>

                        {/* Email — read only */}
                        <FormControl>
                            <FormLabel
                                fontSize="xs"
                                fontWeight="700"
                                color="gray.500"
                                textTransform="uppercase"
                                letterSpacing="wider"
                                mb={2}
                            >
                                <HStack spacing={1}>
                                    <Icon as={MdOutlineEmail} boxSize={4} />
                                    <Text mb={0}>Email</Text>
                                </HStack>
                            </FormLabel>
                            <Flex
                                align="center"
                                px={4}
                                py={2}
                                bg="gray.100"
                                borderRadius="lg"
                                border="1px solid"
                                borderColor="gray.200"
                                minH="42px"
                                cursor="not-allowed"
                            >
                                <Text fontSize="md" color="gray.500" mb={0}>
                                    {user?.email || "—"}
                                </Text>
                            </Flex>
                            <Text fontSize="xs" color="gray.400" mt={1.5} ml={1}>
                                Email cannot be changed
                            </Text>
                        </FormControl>
                    </VStack>
                </Box>

                {/* ── Password Card ── */}
                <Box bg="white" borderRadius="2xl" boxShadow="md" p={8}>
                    <HStack spacing={2} mb={6}>
                        <Icon as={FiLock} boxSize={5} color="purple.600" />
                        <Text fontSize="lg" fontWeight="700" color="gray.700" mb={0}>
                            Password
                        </Text>
                    </HStack>
                    <Divider mb={6} />
                    {showChangePassword ? (
                        <ForgotPassword
                            initialEmail={user?.email}
                            onCancel={() => setShowChangePassword(false)}
                            onSuccess={logout}
                        />
                    ) : (
                        <Button
                            variant="outline"
                            colorScheme="purple"
                            leftIcon={<Icon as={FiLock} />}
                            onClick={() => setShowChangePassword(true)}
                        >
                            Change Password
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default UserSettingPage;